import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Loader2, Upload, Film, FileText, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_CHUNK_RETRIES = 5;

async function uploadChunkWithRetry(basePath, uploadId, chunk, offset, cancelRef, onRetry) {
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        try {
            const fd = new FormData();
            fd.append('file', chunk, `chunk_${offset}`);
            fd.append('offset', String(offset));
            return await axios.post(`${API}/${basePath}/${uploadId}/part`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000,
            });
        } catch (err) {
            attempt += 1;
            const status = err.response?.status;
            if (status === 401) {
                // Access token expired mid-upload — refresh session and retry
                await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).catch(() => {});
            } else if (status && status !== 429 && status < 500) {
                throw err; // permanent client error (400/404/409/410) — don't retry
            }
            if (attempt > MAX_CHUNK_RETRIES || cancelRef.current.cancelled) throw err;
            if (onRetry) onRetry(attempt);
            await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** (attempt - 1), 15000)));
        }
    }
}

/**
 * Chunked file uploader for videos / PDFs / large files.
 * Uploads sequentially in chunks of `chunk_size` returned by /init.
 *
 * Props:
 *  - kind: 'video' | 'pdf' | 'other' (controls accept + server-side validation)
 *  - accept: HTML accept string (e.g. 'video/*' or 'application/pdf')
 *  - maxSizeMB: client-side guard (default 500)
 *  - onSuccess({ path, url, filename, size }): called when upload finishes
 *  - label: button label when idle
 */
export function ChunkedUploader({ kind = 'video', accept, maxSizeMB = 2048, onSuccess, label, basePath = 'admin/uploads/chunk' }) {
    const [phase, setPhase] = useState('idle'); // idle | uploading | done | error
    const [progress, setProgress] = useState(0);
    const [fileName, setFileName] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [retrying, setRetrying] = useState(false);
    const inputRef = useRef(null);
    const cancelRef = useRef({ cancelled: false, uploadId: null });

    const Icon = kind === 'video' ? Film : FileText;
    const acceptStr = accept || (kind === 'video' ? 'video/mp4,video/quicktime,video/webm,video/x-msvideo' : kind === 'pdf' ? 'application/pdf' : '*/*');

    const handleFile = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > maxSizeMB * 1024 * 1024) {
            toast.error(`File too large — max ${maxSizeMB} MB`);
            return;
        }
        cancelRef.current = { cancelled: false, uploadId: null };
        setFileName(file.name);
        setPhase('uploading');
        setProgress(0);
        setErrorMsg('');

        try {
            // 1) Init
            const initRes = await axios.post(`${API}/${basePath}/init`, {
                filename: file.name,
                content_type: file.type || 'application/octet-stream',
                total_size: file.size,
                kind,
            }, { withCredentials: true });
            const { upload_id, chunk_size } = initRes.data;
            cancelRef.current.uploadId = upload_id;

            // 2) Upload chunks sequentially (each chunk retried up to 5x on network errors)
            let offset = 0;
            while (offset < file.size && !cancelRef.current.cancelled) {
                const end = Math.min(offset + chunk_size, file.size);
                const chunk = file.slice(offset, end);
                setRetrying(false);
                await uploadChunkWithRetry(basePath, upload_id, chunk, offset, cancelRef, () => setRetrying(true));
                offset = end;
                setProgress(Math.round((offset / file.size) * 95)); // hold last 5% for assembly
            }
            setRetrying(false);

            if (cancelRef.current.cancelled) {
                await axios.delete(`${API}/${basePath}/${upload_id}`, { withCredentials: true }).catch(() => {});
                setPhase('idle');
                setProgress(0);
                setFileName('');
                toast.info('Upload cancelled');
                return;
            }

            // 3) Complete (refresh session first — long uploads may outlive the access token)
            await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).catch(() => {});
            const completeRes = await axios.post(`${API}/admin/uploads/chunk/${upload_id}/complete`, {}, { withCredentials: true });
            setProgress(100);
            setPhase('done');
            toast.success('Upload complete');
            if (onSuccess) onSuccess(completeRes.data);
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.detail || err.message || 'Upload failed';
            setErrorMsg(msg);
            setPhase('error');
            setRetrying(false);
            toast.error(msg);
            // Best-effort abort
            if (cancelRef.current.uploadId) {
                axios.delete(`${API}/${basePath}/${cancelRef.current.uploadId}`, { withCredentials: true }).catch(() => {});
            }
        } finally {
            if (inputRef.current) inputRef.current.value = '';
        }
    }, [kind, maxSizeMB, onSuccess]);

    const cancel = () => {
        cancelRef.current.cancelled = true;
    };

    const reset = () => {
        setPhase('idle');
        setProgress(0);
        setFileName('');
        setErrorMsg('');
    };

    return (
        <div data-testid={`chunked-uploader-${kind}`}>
            <input ref={inputRef} type="file" accept={acceptStr} className="hidden" onChange={handleFile} data-testid={`chunked-input-${kind}`} />

            {phase === 'idle' && (
                <Button type="button" variant="outline" className="w-full" onClick={() => inputRef.current?.click()} data-testid={`chunked-upload-btn-${kind}`}>
                    <Icon className="w-4 h-4 mr-2" />
                    {label || (kind === 'video' ? 'Upload Video (up to ' + maxSizeMB + ' MB)' : kind === 'pdf' ? 'Upload PDF' : 'Upload File')}
                </Button>
            )}

            {phase === 'uploading' && (
                <div className="p-4 rounded-lg space-y-2" style={{ backgroundColor: 'var(--background-elevated)', border: '1px solid var(--border-subtle)' }}>
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: 'var(--primary-color)' }} />
                            <span className="text-sm truncate" style={{ color: 'var(--text-primary)' }} data-testid={`uploading-filename-${kind}`}>{fileName}</span>
                        </div>
                        <button type="button" onClick={cancel} className="flex-shrink-0" data-testid={`cancel-upload-${kind}`}>
                            <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                        </button>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                    <div className="flex items-center justify-between text-xs">
                        <span style={{ color: retrying ? 'var(--accent-danger)' : 'var(--text-muted)' }}>
                            {retrying ? 'Connection hiccup — retrying…' : 'Uploading in chunks…'}
                        </span>
                        <span className="font-mono" style={{ color: 'var(--primary-color)' }}>{progress}%</span>
                    </div>
                </div>
            )}

            {phase === 'done' && (
                <div className="p-3 rounded-lg flex items-center justify-between gap-2" style={{ backgroundColor: 'var(--primary-muted)', border: '1px solid var(--border-highlight)' }} data-testid={`upload-done-${kind}`}>
                    <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary-color)' }} />
                        <span className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{fileName}</span>
                    </div>
                    <button type="button" onClick={reset} className="text-xs underline" style={{ color: 'var(--primary-color)' }}>
                        Replace
                    </button>
                </div>
            )}

            {phase === 'error' && (
                <div className="p-3 rounded-lg flex items-center justify-between gap-2" style={{ backgroundColor: 'rgba(255, 51, 102, 0.1)', border: '1px solid rgba(255, 51, 102, 0.3)' }}>
                    <div className="flex items-center gap-2 min-w-0">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--accent-danger)' }} />
                        <span className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{errorMsg}</span>
                    </div>
                    <button type="button" onClick={reset} className="text-xs underline" style={{ color: 'var(--accent-danger)' }}>
                        Retry
                    </button>
                </div>
            )}
        </div>
    );
}
